<template>
    <div
        class="tw-mx-auto tw-w-full tw-px-4 md:tw-px-8"
        :class="hasRelatedLessons ? 'tw-max-w-[1703px]' : 'tw-max-w-[1450px]'"
    >
        <Breadcrumb :is-loading="isLoading" :breadcrumbs="breadcrumbsData" />
        <div
            class="xl:tw-gird-rows-4 tw-mt-3 tw-grid tw-grid-cols-3 tw-flex-col tw-gap-4 xl:tw-grid-cols-[auto_auto_420px]"
        >
            <!-- VIDEO WRAPPER -->
            <section
                class="tw-col-span-3 tw-flex tw-w-full xl:tw-row-span-2"
                :class="isRelatedSectionOpen ? 'xl:tw-col-span-2' : 'tw-mb-4'"
            >
                <!-- Video Content -->
                <div class="tw-w-full">
                    <!--Video-->
                    <div
                        class="tw-relative tw-aspect-video tw-w-full tw-bg-[#EDEDED] dark:tw-bg-[#081825]"
                    >
                        <DraftLabel v-if="showDraft" />

                        <!-- Skeleton Loading -->
                        <div
                            v-if="isLoading"
                            class="tw-absolute tw-left-0 tw-top-0 tw-h-full tw-w-full tw-animate-pulse tw-bg-[#F2F2F2] dark:tw-bg-[#002039]"
                        ></div>
                        <!-- Upgrade Cover  -->
                        <MembershipUpgradeVideoCover
                            v-else-if="noAccess"
                            :thumbnail-url="videoData.thumbnail_url"
                        />
                        <template v-else-if="videoData?.video?.external_id">
                            <!-- YouTube -->
                            <transition
                                v-if="
                                    videoData?.video?.type === 'youtube-video'
                                "
                                appear
                                name="fade"
                            >
                                <YoutubePlayer
                                    ref="mediaElementVueInstance"
                                    :brand="brand"
                                    :theme-color="brand"
                                    :use-intersection-observer="true"
                                    :start-second="lastWatchedPositionInSeconds"
                                    :progress-state="progress_state"
                                    :video-id="videoData?.video?.external_id"
                                    :video-length="videoData.length_in_seconds"
                                    :content-id="videoData?.id"
                                    :end-second="videoData.length_in_seconds"
                                    :is-challenge="isChallenge"
                                    :is-completed="isCompleted"
                                    :total-duration="
                                        videoData.length_in_seconds
                                    "
                                    :seek-to-time="seekToTime"
                                    @play="handleVideoPlay"
                                    @pause="handleVideoPause"
                                    @onVideoEnd="handleVideoEnd"
                                    @onUpdateCurrentTime="updateCurrentTime"
                                    @complete-challenge="
                                        completeChallengeLesson
                                    "
                                />
                            </transition>
                            <!-- Vimeo video (legacy player) -->
                            <transition
                                v-else-if="
                                    videoData?.video?.type === 'vimeo-video' &&
                                    useLegacyVideoPlayer
                                "
                                appear
                                name="fade"
                            >
                                <video-media-element
                                    ref="mediaElementVueInstance"
                                    element-id="lessonPlayer"
                                    :brand="brand"
                                    :theme-color="brand"
                                    :current-second="
                                        lastWatchedPositionInSeconds
                                    "
                                    :progress-state="progress_state"
                                    :seek-to-time="seekToTime"
                                    :is-liked="isLiked"
                                    :check-for-timecode="true"
                                    :poster="videoData?.thumbnail_url"
                                    :is-challenge="isChallenge"
                                    :is-completed="isCompleted"
                                    :sources="
                                        videoData?.video
                                            ?.video_playback_endpoints
                                    "
                                    :hls-manifest-url="
                                        videoData?.video?.hlsManifestUrl
                                    "
                                    :video-id="videoData?.video?.external_id"
                                    :content-id="videoData?.id"
                                    :video-length="videoData?.length_in_seconds"
                                    :chapters="videoData?.chapters"
                                    :user-id="userId"
                                    :like-count="likeData?.likeCount"
                                    @playing="handleVideoPlay"
                                    @pause="handleVideoPause"
                                    @ended="handleVideoEnd"
                                    @onUpdateCurrentTime="updateCurrentTime"
                                    @complete-challenge="
                                        completeChallengeLesson
                                    "
                                >
                                    <div
                                        :class="`widescreen title tw-text-${brand}`"
                                    >
                                        <i
                                            class="fas fa-spinner fa-spin absolute-center"
                                        ></i>
                                    </div>
                                </video-media-element>
                            </transition>
                            <!-- Vimeo -->
                            <transition
                                v-else-if="
                                    videoData?.video?.type === 'vimeo-video' &&
                                    !useLegacyVideoPlayer
                                "
                                appear
                                name="fade"
                            >
                                <video-player
                                    ref="mediaElementVueInstance"
                                    :theme-color="brand"
                                    :brand="brand"
                                    :current-second="
                                        lastWatchedPositionInSeconds
                                    "
                                    :seek-to-time="seekToTime"
                                    :poster="videoData?.thumbnail_url"
                                    :sources="
                                        videoData?.video
                                            ?.video_playback_endpoints
                                    "
                                    :hls-manifest-url="
                                        videoData?.video?.hlsManifestUrl
                                    "
                                    :chapters="videoData?.chapters"
                                    :content-id="videoData?.id"
                                    :user-id="userId"
                                    :video-id="videoData?.video?.external_id"
                                    :is-challenge="isChallenge"
                                    :is-completed="isCompleted"
                                    :video-length="videoData?.length_in_seconds"
                                    :total-duration="
                                        videoData.length_in_seconds
                                    "
                                    @play="handleVideoPlay"
                                    @pause="handleVideoPause"
                                    @onVideoEnd="handleVideoEnd"
                                    @onUpdateCurrentTime="updateCurrentTime"
                                    @complete-challenge="
                                        completeChallengeLesson
                                    "
                                >
                                    <div
                                        :class="`widescreen title tw-text-${brand} tw-mb-2`"
                                    ></div>
                                </video-player>
                            </transition>
                            <div
                                v-else
                                class="tw-flex tw-aspect-video tw-w-full tw-flex-col tw-items-center tw-justify-center tw-text-3xl tw-font-bold tw-text-black dark:tw-text-white"
                            >
                                ERROR LOADING VIDEO...
                            </div>
                        </template>
                        <template v-else>
                            <div
                                class="tw-flex tw-aspect-video tw-w-full tw-flex-col tw-items-center tw-justify-center tw-text-3xl tw-font-bold tw-text-black dark:tw-text-white"
                            >
                                No Video Data
                            </div>
                        </template>
                    </div>

                    <VideoResources
                        :theme-color="brand"
                        :brand="brand"
                        :report-user-email="userEmail"
                        :report-user-name="userDisplayName"
                        :no-access="noAccess"
                        :report-logo="emailLogo"
                        :title="videoData.title"
                        :lesson-type="videoData.type"
                        :thumbnail-url="videoData.thumbnail_url"
                        :description="videoData.description"
                        :instructors="videoData.instructor"
                        :is-liked="isLiked"
                        :like-count="likeData?.likeCount"
                        :content-id="videoData.id"
                        :user-id="userId"
                        :resources="videoData.resources"
                        :difficulty="videoData.difficulty"
                        :is-challenge="isChallenge"
                        :show-practice-button="showPracticeButton"
                        :show-share-button="true"
                        :show-complete-button="isWorkout || isChallenge"
                        report-recipient="support+question-and-answer@drumeo.com"
                        :is-completed="isCompleted"
                        :show-add-to-list="true"
                        :show-info-button="showInfoButton"
                        :current-time-in-seconds="lastWatchedPositionInSeconds"
                        @open-practice-soundslice="
                            openSlice(
                                videoData.title,
                                videoData.chapters?.length,
                                0,
                                false
                            )
                        "
                        @on-like-content="likeContent"
                        @on-challenge-lesson-complete="completeChallengeLesson"
                    />

                    <ContentInfo
                        :breadcrumbs="breadcrumbsData"
                        :content-description="videoData.description"
                        :content-chapters="videoData?.chapters"
                        :instructors="videoData?.instructor"
                    />

                    <VideoButtons
                        v-if="!isWorkout"
                        :prev-lesson-url="
                            nextPreviousLessons?.prevLesson?.web_url_path
                        "
                        :next-lesson-url="nextLessonUrl"
                        :brand="brand"
                        prev-label="Previous Lesson"
                        next-label="Next Lesson"
                    />

                    <ContentProgress
                        v-if="!noAccess && !isWorkout && !isChallenge"
                        :brand="brand"
                        :is-completed="isCompleted"
                        :progress="progress_percent"
                        :xp-amount="videoData?.xp"
                        :is-started="progress_percent > 0"
                        :next-lesson-url="
                            nextPreviousLessons?.nextLesson?.web_url_path
                        "
                        :show-complete-button="true"
                        :content-id="videoData.id"
                    />
                </div>
                <!-- Related Lessons Toggle -->
                <RelatedLessonsToggle
                    v-if="hasRelatedLessons"
                    :is-loading="isLoading"
                    :relatedLessons="relatedLessons"
                    :isRelatedSectionOpen="isRelatedSectionOpen"
                    v-model:isRelatedSectionOpen="isRelatedSectionOpen"
                />
            </section>

            <!--Related Section -->
            <RelatedLessons
                :isRelatedSectionOpen="isRelatedSectionOpen"
                :is-loading="isLoading"
                :relatedLessons="relatedLessons"
                v-model:isRelatedSectionOpen="isRelatedSectionOpen"
                :is-challenge="isChallenge"
            />

            <!-- Lesson Content Wrapper -->
            <section
                v-if="!noAccess"
                class="tw-col-span-3 xl:tw-row-span-2"
                :class="
                    isRelatedSectionOpen
                        ? 'xl:tw-col-span-2'
                        : `${hasRelatedLessons ? 'xl:tw-mr-[64px]' : ''}`
                "
            >
                <!-- Chapters -->
                <VideoChapters
                    v-if="
                        videoData?.chapters?.length &&
                        (isWorkout || isChallengePart)
                    "
                    :chapters="videoData.chapters"
                    @open-slice="openSlice"
                    @seek-to-chapter="seekToChapter"
                    :soundslice-slug="videoData?.soundslice_slug"
                />

                <!-- Assignments -->
                <div
                    v-if="videoData?.assignments?.length > 0 && !isWorkout"
                    class="tw-mt-3 tw-flex tw-w-full tw-flex-grow tw-flex-col"
                >
                    <div
                        class="tw-flex tw-w-full tw-flex-row tw-items-center tw-justify-between tw-border-b tw-border-[#e5e8e8] tw-pb-4 dark:tw-border-[#223F57]"
                    >
                        <h1 class="heading dark:tw-text-white">Assignments</h1>
                        <button
                            class="tw-z-10"
                            @click="
                                state.assignmentCollapsed =
                                    !state.assignmentCollapsed
                            "
                        >
                            <div
                                class="tw-flex tw-h-[35px] tw-w-[35px] tw-items-center tw-justify-center tw-rounded-full tw-border-2 tw-border-[#000C17] tw-text-[#000C17] dark:tw-border-white dark:tw-text-white sm:tw-h-[50px] sm:tw-w-[50px]"
                                :class="
                                    !state.assignmentCollapsed &&
                                    'tw-rotate-180'
                                "
                            >
                                <i class="fas fa-chevron-down"></i>
                            </div>
                        </button>
                    </div>
                    <div
                        class="tw-w-full tw-flex-row"
                        :class="
                            state.assignmentCollapsed ? 'tw-hidden' : 'tw-flex'
                        "
                    >
                        <AssignmentsContainer
                            :lesson-data="videoData"
                            :assignments="videoData?.assignments"
                            :brand="brand"
                            :user-id="userId"
                        />
                    </div>
                </div>
                <div class="tw-flex tw-w-full tw-flex-grow tw-flex-col">
                    <div class="tw-flex tw-w-full tw-flex-row">
                        <VideoComments
                            :is-loading="isLoading"
                            :theme-color="brand"
                            :brand="brand"
                            :user-id="userId"
                            :is-admin="isAdmin"
                            :content-id="videoData?.railcontent_id"
                            :user-name="userDisplayName"
                            :user-avatar="userProfilePictureUrl"
                            :user-xp="userXP"
                            :user-access-level="userAccessLevel"
                            :profile-base-route="`/${brand}/profile/${userId}/dashboard`"
                        />
                    </div>
                </div>
            </section>
        </div>
        <LessonComplete
            v-if="!isWorkout && videoData.id && videoData.type"
            :next-lesson-json="nextPreviousLessons?.nextLesson"
            :contentId="videoData.id"
            :contentType="videoData.type"
        />

        <!-- Chapter Soundslice -->
        <transition name="show-from-bottom">
            <div v-if="openSoundslice" id="practiceOverlay" class="bg-white">
                <SoundSlice
                    :key="`${Math.floor(chapterStartTime)}${Math.floor(
                        chapterEndTime
                    )}${startLooping ? 'loop' : 'noloop'}`"
                    :user-id="userId"
                    :theme-color="brand"
                    :additional-params="`${getBrandSpecificParams()}&layout=3`"
                    :soundslice-slug="videoData?.soundslice_slug"
                    :contentId="videoData?.id"
                    :start-time="chapterStartTime"
                    :end-time="chapterEndTime"
                    :loop="startLooping"
                    :is-challenge="isChallenge"
                    :is-completed="isCompleted"
                    @on-challenge-lesson-complete="completeChallengeLesson"
                >
                    <template v-slot:soundsliceControls>
                        <SoundSliceControls
                            :title="soundsliceTitle || videoData.title"
                            :disable-next="true"
                            :disable-prev="true"
                            @onClose="handleCloseSoundslice"
                        />
                    </template>
                </SoundSlice>
            </div>
        </transition>
    </div>

    <ChallengeCompletionModal
        v-if="isChallengeCompletionModalOpen"
        :completion-data="completionData"
        @close-modal="closeChallengeCompletionModal"
    />
</template>

<script setup>
// TODO: ADD THE PLAY AND PAUSE EVENTS TO THE VIDEO PLAYERS
import { ref, computed, reactive, onBeforeMount } from "vue";
import { storeToRefs } from "pinia";
import { useUserStore } from "@stores/user";
import { usePlatformStore } from "@stores/platform";
import Breadcrumb from "@collections/Breadcrumb/Breadcrumb.vue";
import YoutubePlayer from "@vuesora/Components/YoutubePlayer/YoutubePlayer.vue";
import VideoButtons from "@collections/VideoButtons/VideoButtons.vue";
import VideoResources from "@vuesora/Components/VideoResources/VideoResources.vue";
import VideoComments from "@vuesora/views/comments/Comments.vue";
import ContentInfo from "@collections/ContentInfo/ContentInfo.vue";
import Intercom from "@vuesora/assets/js/Services/intercom";
import Helpscout from "@vuesora/assets/js/Services/helpscout";
import ProgressTracker from "@vuesora/assets/js/classes/progress-tracker";
import ContentProgress from "@collections/ContentProgress/ContentProgress.vue";
import RelatedLessonsToggle from "@collections/RelatedLessons/RelatedLessonsToggle.vue";
import RelatedLessons from "@collections/RelatedLessons/RelatedLessons.vue";
import LessonComplete from "@collections/ContentProgress/LessonComplete.vue";
import VideoChapters from "@collections/VideoChapters/VideoChapters.vue";
import MembershipUpgradeVideoCover from "@collections/MembershipUpgradeVideoCover/MembershipUpgradeVideoCover";
import SoundSlice from "@collections/SoundSlice/SoundSlice.vue";
import SoundSliceControls from "@collections/SoundSlice/SoundSliceControls.vue";
import ChallengeCompletionModal from "@collections/Modal/ChallengeCompletionModal";
import DraftLabel from "@units/DraftLabel/DraftLabel";
import {
    fetchLessonContent,
    fetchRelatedLessons,
    fetchRelatedTutorials,
    fetchNextPreviousLesson,
    isContentLiked,
    fetchChallengeLessonData,
    postChallengesCompleteLesson,
    getProgressState,
    // this is not getting percentage, this is getting lats watched position in seconds, possibly rename this function
    getProgressPercentage,
} from "musora-content-services";
import { getContentId } from "@hooks/utils";
import { getBreadcrumbs } from "./breadcrumbUtils";

const props = defineProps({
    lessonType: {
        type: String,
        default: "",
    },
});

//Pinia
const userStore = useUserStore();
const platformStore = usePlatformStore();
const { isLoading } = storeToRefs(platformStore);
const {
    userId,
    userEmail,
    isAdmin,
    userDisplayName,
    userAccessLevel,
    userXP,
    userProfilePictureUrl,
    brand,
    useLegacyVideoPlayer,
} = storeToRefs(userStore);

let hasBeenPlayed = false;
let progressTracker;

//Refs
const progress_percent = ref(0);
const progress_state = ref(0);
const lastWatchedPositionInSeconds = ref(0);
const isRelatedSectionOpen = ref(true);
const isChallengeCompletionModalOpen = ref(false);
const openSoundslice = ref(false);
const seekToTime = ref(0);
const chapterStartTime = ref(0);
const chapterEndTime = ref(0);
const soundsliceTitle = ref("");
const startLooping = ref(false);
const mediaElementVueInstance = ref(null);
const contentId = ref(getContentId());

const videoData = ref({});
const likeData = ref({});
const isLiked = ref(false);
const isCompleted = ref(false);
const relatedLessons = ref([]);
const nextPreviousLessons = ref(null);
const completionData = ref(null);

//Reactive
const state = reactive({
    assignmentCollapsed: false,
});

const showPracticeButton = computed(() => {
    return videoData.value?.soundslice_slug;
});

const showInfoButton = computed(() => {
    return (
        videoData.value?.instructor?.length > 0 ||
        videoData.value?.description?.length > 0 ||
        videoData.value?.chapters?.length > 0
    );
});

const nextLessonUrl = computed(() => {
    if (isChallenge.value && isNextLessonLocked.value && !isAdmin.value) {
        return null;
    }

    return nextPreviousLessons.value?.nextLesson?.web_url_path;
});

const isNextLessonLocked = computed(() => {
    return nextPreviousLessons.value?.nextLesson?.is_locked;
});

const showDraft = computed(() => {
    return videoData.value?.status?.toLowerCase() === "draft";
});

//Methods
const handleVideoPlay = (payload) => {
    if (
        ["started", "completed"].indexOf(payload.progressState) === -1 &&
        !hasBeenPlayed
    ) {
        sendProgressTrackerEvent();
    }
    if (progressTracker == null) {
        progressTracker = new ProgressTracker();
        if (mediaElementVueInstance.value) {
            attachVisibilityAndPagehideEvents();
        }
    }
    hasBeenPlayed = true;
    progressTracker.start();
};

const sendProgressTrackerEvent = () => {
    if (progressTracker) {
        progressTracker.send({
            mediaType: "video",
            mediaCategory: videoData.value?.video?.type.split("-")[0],
            watchPosition:
                mediaElementVueInstance.value.currentTimeInSeconds ||
                mediaElementVueInstance.value.currentTime,
            totalDuration:
                mediaElementVueInstance.value.videoLength ||
                mediaElementVueInstance.value.totalDuration,
            brand: brand.value,
            contentId: mediaElementVueInstance.value.contentId,
        });
    }
};

const attachVisibilityAndPagehideEvents = () => {
    document.addEventListener("visibilitychange", () => {
        sendProgressTrackerEvent();
    });

    window.addEventListener("pagehide", () => {
        sendProgressTrackerEvent();
    });
};

const handleVideoPause = () => {
    progressTracker.stop();
    sendProgressTrackerEvent();
};
const handleVideoEnd = (showRelatedSection = true) => {
    if (showRelatedSection) {
        isRelatedSectionOpen.value = true;
    }
    sendProgressTrackerEvent();
};

const getBrandSpecificParams = () => {
    return {
        drumeo: "&show_chords=0",
        singeo: "&show_staff_t1=0&show_staff_t2=0&show_chords=0",
        guitareo: "",
        pianote: "&show_chords=1",
    }[brand.value];
};

const openSlice = (title, index, startAt, loop) => {
    if (mediaElementVueInstance.value) {
        mediaElementVueInstance.value.pauseVideo();
    }
    soundsliceTitle.value = title;
    chapterStartTime.value = startAt;
    chapterEndTime.value = videoData.value?.length_in_seconds;
    startLooping.value = loop;

    if (loop) {
        chapterEndTime.value =
            videoData.value?.chapters.length === index
                ? videoData.value?.length_in_seconds
                : videoData.value?.chapters[index]?.chapter_timecode;
    }

    openSoundslice.value = true;
};

const seekToChapter = (time) => {
    isRelatedSectionOpen.value = false; //Run by Mitch
    seekToTime.value = time;
};

const handleCloseSoundslice = () => {
    openSoundslice.value = false;
    startLooping.value = false;

    document.body.classList.remove("no-scroll", "dim-sidebar");

    Helpscout.showWidget();
    Intercom.showWidget();
};

//Computed
const emailLogo = computed(() => {
    if (brand.value === "singeo") {
        return "https://dmmior4id2ysr.cloudfront.net/logos/singeo-logo-purple.png";
    } else {
        return `https://dmmior4id2ysr.cloudfront.net/logos/${brand.value}-logo.png`;
    }
});

const hasRelatedLessons = computed(() => {
    return relatedLessons.value.length > 0;
});

const noAccess = computed(() => {
    return videoData.value?.need_access;
});

const isChallenge = computed(() => {
    return props.lessonType === "challenge";
});

const isTutorialChild = computed(() => {
    return props.lessonType === "song-tutorial-children";
});

const likeContent = () => {
    isLiked.value = !isLiked.value;

    if (isLiked.value) {
        likeData.value.likeCount += 1;
    } else {
        likeData.value.likeCount -= 1;
    }
};

const completeChallengeLesson = async () => {
    try {
        const complete = await postChallengesCompleteLesson(videoData.value.id);
        isCompleted.value = !isCompleted.value;

        if (complete.show_modal) {
            completionData.value = complete;
            isChallengeCompletionModalOpen.value = true;
        }
    } catch (e) {
        window.shownotification({
            icon: "error",
            text: "Woops! Something wrong happened, please try again later.",
        });
    }
};

const closeChallengeCompletionModal = () => {
    isChallengeCompletionModalOpen.value = false;
};

const isWorkout = computed(() => {
    return videoData.value.type === "workout";
});

const isChallengePart = computed(() => {
    return videoData.value.type === "challenge-part";
});

const breadcrumbsData = computed(() => {
    return getBreadcrumbs(videoData.value, brand.value);
});

const updateCurrentTime = (time) => {
    lastWatchedPositionInSeconds.value = Math.floor(time);
};

const fetchLessonData = async () => {
    if (isChallenge.value) {
        const results = await Promise.allSettled([
            fetchChallengeLessonData(contentId.value),
            axios.get(`/content/${contentId.value}/user_data/${userId.value}`),
            isContentLiked(contentId.value),
        ]);

        const [dataResult, likeResult, likedResult] = results;

        videoData.value =
            dataResult.status === "fulfilled"
                ? {
                      ...dataResult.value.lesson,
                      instructor:
                          dataResult?.value?.lesson?.challenge_instructor,
                  }
                : null;
        likeData.value =
            likeResult.status === "fulfilled" ? likeResult.value.data : null;
        isLiked.value =
            likedResult.status === "fulfilled" ? likedResult.value : false;
        isCompleted.value =
            dataResult.status === "fulfilled" &&
            dataResult.value.lesson.completed;
        nextPreviousLessons.value =
            dataResult.status === "fulfilled"
                ? {
                      nextLesson: dataResult.value.next_lesson,
                      prevLesson: dataResult.value.previous_lesson,
                  }
                : null;

        relatedLessons.value =
            dataResult.status === "fulfilled"
                ? await dataResult.value.lessons
                : [];
    } else {
        let relatedPromise = isTutorialChild.value
            ? fetchRelatedTutorials
            : fetchRelatedLessons;

        // Execute all video calls using Promise.allSettled
        const results = await Promise.allSettled([
            fetchLessonContent(contentId.value),
            axios.get(`/content/${contentId.value}/user_data/${userId.value}`),
            isContentLiked(contentId.value),
            fetchNextPreviousLesson(contentId.value),
            relatedPromise(contentId.value, brand.value),
            getProgressPercentage(contentId.value),
            getProgressState(contentId.value),
        ]);

        // Process results
        const [
            dataResult,
            likeResult,
            likedResult,
            nextPrevResult,
            relatedLessonsResult,
            progressResult,
            progressStateResult,
        ] = results;

        // needs refactoring
        videoData.value =
            dataResult.status === "fulfilled" ? dataResult.value : null;
        likeData.value =
            likeResult.status === "fulfilled" ? likeResult.value.data : null;
        isLiked.value =
            likedResult.status === "fulfilled" ? likedResult.value : false;
        nextPreviousLessons.value =
            nextPrevResult.status === "fulfilled" ? nextPrevResult.value : null;
        relatedLessons.value =
            relatedLessonsResult.status === "fulfilled"
                ? relatedLessonsResult.value.related_lessons
                : [];
        progress_state.value =
            progressStateResult.status === "fulfilled" &&
            progressStateResult.value
                ? progressStateResult.value
                : "unstarted";

        if (progressResult.status === "fulfilled") {
            progress_percent.value = progressResult.value;
            lastWatchedPositionInSeconds.value = progressResult.value;
            isCompleted.value = progress_state.value === "completed";
        }

        // Optional: log errors for any rejected promises
        results.forEach((result, index) => {
            if (results.status === "rejected") {
                console.error(
                    `Promise at index ${index} failed:`,
                    results.reason
                );
            }
        });
    }

    if (relatedLessons.value.length === 0) {
        isRelatedSectionOpen.value = false;
    }

    platformStore.setLoadingState(false);
};

onBeforeMount(() => {
    fetchLessonData();
});
</script>
